/*
 * The shop's pill filter, and the docked pill that stands in for it.
 *
 * Hand-written, unlike synonym-chrome.js: the magazine's initStoryFilter() is
 * wound through WordPress query vars, its own pagination and a fetch of the next
 * page, none of which exist here. What is shared is what matters — the class
 * names, so the pills and the dock are drawn by the magazine's own rules, and
 * the contract that every facet is a URL first and an in-place filter second.
 *
 * Without this file the pills are still links to their collections, the page
 * still works, and the dock never appears — the same no-JS fallback the
 * magazine keeps.
 */
(function () {
  const grid = document.querySelector('[data-product-grid]');
  const filters = [...document.querySelectorAll('[data-product-filter]')];
  if (!grid || !filters.length) return;

  // px from the top of the window. Once the page filter's bottom edge has passed
  // this line it counts as "near the top" and the docked pill takes over.
  const DOCK_REVEAL = 120;
  // Below this the pill is the only filter there is — the row at the top of the
  // page isn't shown at all, because two rows of pills above the first card push
  // the products themselves off the first screen.
  const DOCK_QUERY = '(max-width: 900px)';
  // ms — keep in step with the sheet's opacity transition in style.css.
  const SHEET_FADE = 220;
  const CLOSE_FILTERS = 'Close filters';

  const empty = document.querySelector('[data-filter-empty]');
  const items = [...grid.children];

  // The facets a card belongs to, read once. Reading data-collections on every
  // pass would re-parse the same string for every card on every click.
  const memberships = new Map(
    items.map((el) => [el, new Set((el.dataset.collections || '').split(/\s+/).filter(Boolean))])
  );

  /*
   * Each facet's own running order, as the admin dragged it.
   *
   * A collection sorted "Manually" is an editorial decision about what a reader
   * meets first, and it differs per collection — Issues wants newest issue
   * first, Subscriptions wants cheapest tier first. So picking a pill re-orders
   * the grid into that collection's order rather than leaving it in the one the
   * unfiltered page happens to use.
   *
   * The nodes are physically moved rather than given a CSS `order`. Same reason
   * the price sits above the title in the template's block order and not in the
   * stylesheet: `order` moves the picture and leaves the reading order and the
   * tab order behind, so a screen reader would hear the grid in an order nobody
   * chose while the page showed another.
   */
  const orderNode = document.querySelector('[data-filter-order]');
  let facetOrder = new Map();
  if (orderNode) {
    try {
      facetOrder = new Map(
        Object.entries(JSON.parse(orderNode.textContent)).map(([handle, ids]) => [
          handle,
          new Map(ids.map((id, i) => [String(id), i])),
        ])
      );
    } catch (e) {
      // A malformed map is not worth taking the filter down for — the grid
      // keeps the order it was rendered in and every pill still filters.
      facetOrder = new Map();
    }
  }

  // The order the page arrived in, which is the master collection's. Captured
  // before anything moves, because it is what clearing the filter restores.
  const baseOrder = items.slice();

  function reorder(handle) {
    const positions = handle && facetOrder.get(handle);
    const next = positions
      ? // Anything the collection doesn't rank sorts to the end rather than to
        // the front, which is where an unranked item would land on -1.
        baseOrder.slice().sort((a, b) => {
          const ai = positions.has(a.dataset.id) ? positions.get(a.dataset.id) : Infinity;
          const bi = positions.has(b.dataset.id) ? positions.get(b.dataset.id) : Infinity;
          return ai - bi;
        })
      : baseOrder;

    // Nothing to do if the grid already reads this way — the common case on
    // first paint and on every clear, and it saves moving 25 nodes to put them
    // back where they were.
    const current = [...grid.children];
    if (next.every((el, i) => current[i] === el)) return;

    const frag = document.createDocumentFragment();
    next.forEach((el) => frag.appendChild(el));
    grid.appendChild(frag);
  }

  // Every pill for a given facet, across both copies of the filter — the page
  // row and the one in the dock's sheet. They are the same control rendered
  // twice, so they are bound and updated together.
  const pillsByFacet = new Map();
  filters.forEach((f) => {
    f.querySelectorAll('[data-facet]').forEach((pill) => {
      const handle = pill.dataset.facet;
      if (!pillsByFacet.has(handle)) pillsByFacet.set(handle, []);
      pillsByFacet.get(handle).push(pill);
    });
  });
  if (!pillsByFacet.size) return;

  const allPills = filters.flatMap((f) => [...f.querySelectorAll('[data-facet-all]')]);

  const PARAM = 'c';

  function selectedFromUrl() {
    const raw = new URLSearchParams(location.search).get(PARAM);
    if (!raw) return new Set();
    // Only the first survives. ?c= used to carry a list, and a link saved from
    // back then would otherwise restore two selections into a filter that can
    // no longer produce them — and, since the note needs exactly one, would
    // land on a state with no description showing and no way to tell why.
    const known = raw.split(',').filter((h) => pillsByFacet.has(h));
    return new Set(known.slice(0, 1));
  }

  let selected = selectedFromUrl();

  /*
   * Re-count each pill from the grid it actually filters.
   *
   * Liquid renders the collection's own product count, which is the number
   * Shopify holds — and that includes anything excluded from this grid. A pill
   * saying 8 that reveals 7 products is worse than no number at all, and the
   * gap would only appear the day somebody files a wholesale product into a
   * reader-facing collection as well.
   *
   * Free to do here: the circle has zero width until its pill is active, so
   * nothing visible changes under the reader.
   */
  pillsByFacet.forEach((pills, handle) => {
    const n = items.filter((el) => memberships.get(el).has(handle)).length;
    pills.forEach((pill) => {
      const value = pill.querySelector('.story-filter__count-value');
      if (value) value.textContent = String(n);
      const spoken = pill.querySelector('.visually-hidden');
      if (spoken) spoken.textContent = `${n} ${n === 1 ? 'product' : 'products'}`;
    });
  });

  // handle -> its description block. Only collections that have one are here,
  // so a pick with nothing to say simply leaves the map without a match.
  const notes = new Map(
    [...document.querySelectorAll('[data-collection-note]')]
      .map((el) => [el.dataset.collectionNote, el])
  );

  const dock = document.querySelector('[data-filter-dock]');
  const dockToggle = dock && dock.querySelector('.story-filter-dock__toggle');
  const dockSheet = dock && dock.querySelector('.story-filter-dock__sheet');
  const dockCount = dockToggle && dockToggle.querySelector('.story-filter__count-value');

  function apply({ push = false } = {}) {
    pillsByFacet.forEach((pills, handle) => {
      const on = selected.has(handle);
      pills.forEach((pill) => {
        pill.classList.toggle('is-active', on);
        pill.setAttribute('aria-pressed', String(on));
      });
    });

    // Nothing selected already means all of them, so the clearing pill is lit
    // exactly when no facet is.
    allPills.forEach((p) => p.classList.toggle('is-active', selected.size === 0));

    let shown = 0;
    items.forEach((el) => {
      const inSet = memberships.get(el);
      // OR, not AND: picking Issues and Prints asks for both rows, not for the
      // products somehow in both.
      const visible = selected.size === 0 || [...selected].some((h) => inSet.has(h));
      el.hidden = !visible;
      if (visible) shown += 1;
    });

    if (empty) empty.hidden = shown !== 0;

    // One facet is the most the filter can hold, so the selection either names
    // an order or there is none and the master order stands.
    reorder(selected.size === 1 ? [...selected][0] : null);

    // The chosen collection's description, if it has one. Driven from here
    // rather than from the click handler so it is right after a back button and
    // on first paint too, both of which call apply() without a click.
    if (notes.size) {
      const on = selected.size === 1 ? [...selected][0] : null;
      notes.forEach((note, handle) => { note.hidden = handle !== on; });
    }

    // The dock's own count circle, saying the same kind of thing the section
    // pills' circles say: how many facets are on.
    if (dockToggle) {
      dockToggle.classList.toggle('is-active', selected.size > 0);
      if (dockCount) dockCount.textContent = String(selected.size);
    }

    const url = new URL(location.href);
    if (selected.size) {
      url.searchParams.set(PARAM, [...selected].join(','));
    } else {
      url.searchParams.delete(PARAM);
    }
    history[push ? 'pushState' : 'replaceState']({ [PARAM]: [...selected] }, '', url);
  }

  /* Filled in once the dock exists. A pill in the sheet has to be able to shut
     it, and closeSheet() is declared inside the dock's own block below — this is
     the handle across, rather than hoisting the whole thing up here for the sake
     of one call. */
  let dismissDockSheet = () => {};

  function bind(pill, handler) {
    pill.addEventListener('click', (e) => {
      // A modified click is somebody opening the collection in a tab, which is
      // what the href is still there for.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      handler();
      apply({ push: true });

      /*
       * Picked from the docked pill, the page goes back to the top.
       *
       * The dock only exists once the reader has scrolled past the filter row,
       * so a pick from it lands them in the middle of a set they have never
       * seen the start of — and with the grid re-flowing under them, the middle
       * of the new set is nowhere they recognise. The Stories page does the
       * same thing for the same reason.
       *
       * The sheet goes first: below 900px it covers the page, and scrolling
       * behind something opaque is a journey nobody can see. Only for the
       * dock's copy of the pills — the row at the top of the page is already
       * at the top of the page.
       */
      if (!pill.closest('[data-filter-dock]')) return;

      dismissDockSheet();
      window.scrollTo({
        top: 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
      });
    });
  }

  // One at a time. Picking a collection replaces whatever was picked before
  // rather than adding to it, and picking the lit one again clears back to all
  // — which is the same place the All Products pill goes, reached the other way.
  //
  // The set survives the change to a single value because everything reading it
  // already handles "none": the OR below, the dock's count, and the ?c= on the
  // URL all behave the same whether it holds one handle or none.
  pillsByFacet.forEach((pills, handle) => {
    pills.forEach((pill) =>
      bind(pill, () => {
        const wasOn = selected.has(handle);
        selected.clear();
        if (!wasOn) selected.add(handle);
      })
    );
  });

  allPills.forEach((pill) => bind(pill, () => { selected = new Set(); }));

  window.addEventListener('popstate', () => {
    selected = selectedFromUrl();
    apply();
  });

  /* ------------------------------------------------------------------------
     The docked pill.
     ------------------------------------------------------------------------ */
  if (dock && dockToggle && dockSheet) {
    // Only now does the dock become visible at all — with no script the page
    // keeps the ordinary filter at the top, at every width.
    document.documentElement.classList.add('has-filter-dock');

    const isNarrow = window.matchMedia(DOCK_QUERY);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let hideTimer = null;
    let lastFocused = null;
    let nearTop = false;
    let atEnd = false;

    function openSheet() {
      lastFocused = document.activeElement;
      clearTimeout(hideTimer);
      dockSheet.hidden = false;

      /*
       * Says the panel is on screen, which is a longer window than .is-open:
       * it starts here and ends a fade after that class has gone.
       *
       * The stylesheet uses it to take the transform off the dock. Below 900px
       * the sheet is `position: fixed; inset: 0`, and the dock is both fixed
       * and transformed — a transform makes an element the containing block
       * for anything fixed inside it, so "inset: 0" meant the dock's own strip
       * at the bottom of the window rather than the window. Without this class
       * the sheet opened 208px tall below the fold, which is why the pill read
       * as doing nothing at all on a phone.
       */
      dock.classList.add('is-showing');

      /*
       * The panel has to be laid out at its starting state before the class
       * that animates it lands. Set in the same breath as `hidden`, there is
       * nothing to transition from and the sequence snaps on at once. Reading a
       * layout property forces that, and forces it now rather than a frame
       * later — on a phone the tap usually lands right after a scroll, with the
       * main thread busy, and a rAF there is a frame the reader waits through.
       */
      void dockSheet.offsetWidth;
      dock.classList.add('is-open');
      dockToggle.setAttribute('aria-expanded', 'true');
      dockToggle.setAttribute('aria-label', CLOSE_FILTERS);

      // Below the breakpoint the sheet is a full-screen modal and the role goes
      // on the dock — the whole thing, pill included, because the pill is what
      // closes it and a dialog's close control has to be inside it. Above the
      // breakpoint it is a popover above the pill, and claiming to be modal
      // would be a lie, so the role is set here rather than in the markup.
      if (isNarrow.matches) {
        dock.setAttribute('role', 'dialog');
        dock.setAttribute('aria-modal', 'true');
        /*
         * The magazine's own class for this lock, and deliberately not the
         * nav's `menu-open`: the stylesheet hides the docked pill whenever the
         * site menu is up, and sharing one class would have had the pill hide
         * itself the moment it was pressed. Both do the same job — overflow:
         * hidden on the body — they just have to be tellable apart.
         */
        document.body.classList.add('filters-open');
      }
    }

    dismissDockSheet = closeSheet;

    function closeSheet({ focusToggle = false } = {}) {
      if (dockSheet.hidden) return;
      dock.classList.remove('is-open');
      dockToggle.setAttribute('aria-expanded', 'false');
      dockToggle.removeAttribute('aria-label');
      dock.removeAttribute('role');
      dock.removeAttribute('aria-modal');
      document.body.classList.remove('filters-open');

      // Held in the page until the fade finishes — hiding it immediately would
      // cut the transition off on its first frame.
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        dockSheet.hidden = true;
        // Last, not with .is-open above: the sheet is still fading and still on
        // screen at that point, and putting the dock's transform back while it
        // is would run the open animation in reverse — the panel shrinking into
        // the strip at the bottom as it went.
        dock.classList.remove('is-showing');
      }, prefersReducedMotion.matches ? 0 : SHEET_FADE);

      const returnTo = focusToggle ? dockToggle : lastFocused;
      if (returnTo && returnTo.focus) returnTo.focus();
    }

    dockToggle.addEventListener('click', () => {
      // It is the close button at this point, and focus belongs on it after.
      if (dock.classList.contains('is-open')) closeSheet({ focusToggle: true });
      else openSheet();
    });

    // Full screen, the panel is its own backdrop: a tap on the empty part of it
    // is a tap outside the controls, which is how every other overlay closes.
    dockSheet.addEventListener('click', (e) => {
      if (e.target === dockSheet) closeSheet({ focusToggle: true });
    });

    // Picking a facet in the sheet has said what it came to say.
    dockSheet.addEventListener('click', (e) => {
      if (e.target.closest('[data-facet], [data-facet-all]')) closeSheet({ focusToggle: true });
    });

    document.addEventListener('keydown', (e) => {
      if (dockSheet.hidden) return;
      if (e.key === 'Escape') {
        closeSheet({ focusToggle: true });
        return;
      }
      // While it claims aria-modal, tabbing has to stay inside it — and "it" is
      // the dock, so the cross the pill has turned into is part of the round trip.
      if (e.key !== 'Tab' || dock.getAttribute('role') !== 'dialog') return;
      const focusable = dock.querySelectorAll('a[href], button');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Crossing the breakpoint with it open would leave a modal's scroll lock on
    // a popover, or a popover's semantics on something covering the page.
    isNarrow.addEventListener('change', () => {
      if (!dockSheet.hidden) closeSheet();
      syncDock();
    });

    function syncDock() {
      const show = isNarrow.matches || nearTop;
      dock.classList.toggle('is-visible', show);

      // Except once the list has ended. Past that point the reader is looking
      // at something other than products, and a pill floating over it is in the
      // way. Its own class rather than folding into `show`, because below the
      // breakpoint the pill's resting state is "visible" and this has to be
      // able to overrule that.
      dock.classList.toggle('is-hidden', atEnd);

      if (!show || atEnd) closeSheet();
    }

    const pageFilter = document.querySelector('.story-filter--page');

    if (pageFilter && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // The observer fires as the filter's bottom edge crosses the line,
            // so this reads the edge rather than the boolean: has it gone past.
            nearTop = entry.boundingClientRect.bottom <= DOCK_REVEAL;
          });
          syncDock();
        },
        { threshold: 0, rootMargin: `-${DOCK_REVEAL}px 0px 0px 0px` }
      ).observe(pageFilter);
    } else {
      // No filter at the top to watch, or no observer: the pill is all there is.
      nearTop = true;
    }

    // What counts as the end of the list. The footer was the only marker here,
    // which was right when the grid ran straight into it — but the shop's
    // landing page now finishes on a callout panel, and watching the footer
    // alone left the pill hovering over that the whole way down it.
    //
    // Both are observed rather than just the first, because which one comes
    // first is a property of the page and not of this script: a template
    // without the panel still has the footer, and a longer page may put
    // something else between them later.
    const enders = ['.synonym-wholesale', '.site-footer']
      .map((sel) => document.querySelector(sel))
      .filter(Boolean);

    if (enders.length && 'IntersectionObserver' in window) {
      // A set, not a boolean: with two things being watched, the last entry to
      // fire is not necessarily the one that is on screen — scrolling the panel
      // out of view while the footer is in it would otherwise bring the pill
      // back over the footer.
      const showing = new Set();
      const endWatcher = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) showing.add(entry.target);
            else showing.delete(entry.target);
          });
          atEnd = showing.size > 0;
          syncDock();
        },
        { threshold: 0 }
      );
      enders.forEach((el) => endWatcher.observe(el));
    }

    syncDock();
  }

  apply();
})();
